import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const FEEDBACK_TO = 'marcusg.2387@gmail.com';

const TYPES = [
  { value: 'bug', label: 'Something’s broken' },
  { value: 'feature', label: 'Feature idea' },
  { value: 'other', label: 'Something else' },
];

// Lightweight in-app feedback channel -- no new table, no admin review
// UI. Reuses the existing SendEmail integration (already wired for
// driver/payment notifications) to land the message straight in an
// inbox. Cheap on purpose: this is the same "we're listening" signal
// competitor research found solo owner-operators respond to, without
// building a support ticketing system nobody asked for yet.
export default function FeedbackModal({ onClose }) {
  const { user, carrierName } = useAuth();
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error('Add a few words first.'); return; }
    setSending(true);
    try {
      const typeLabel = TYPES.find(t => t.value === type)?.label || 'Feedback';
      await client.integrations.Core.SendEmail({
        to: FEEDBACK_TO,
        subject: `[Pro Haul Feedback] ${typeLabel} — ${carrierName || 'Unknown carrier'}`,
        body: `<p><strong>From:</strong> ${user?.email || 'unknown'}<br/><strong>Carrier:</strong> ${carrierName || 'unknown'}<br/><strong>Type:</strong> ${typeLabel}</p><p>${message.trim().replace(/\n/g, '<br/>')}</p>`,
      });
      toast.success('Sent — thanks for the note.');
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Could not send that. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-start justify-center p-4">
        <div className="relative w-full max-w-md my-8 z-10 rounded-2xl border border-border bg-background">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <div>
              <h2 className="font-space font-bold text-foreground text-lg">Send feedback</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Goes straight to the team, not a ticket queue.</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="flex gap-2">
              {TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    type === t.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-transparent text-muted-foreground border-border hover:text-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="What's going on?"
              rows={5}
              autoFocus
              className="w-full rounded-lg bg-transparent border border-input text-foreground text-sm p-3 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
            <Button type="submit" disabled={sending} className="w-full bg-primary text-primary-foreground">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : 'Send'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
